import type {
  CreatePlanRequest,
  Plan,
  PlanDetail,
  PlanPriceMigration,
  PlanPricingImpact,
  UpdatePlanDetailsRequest,
  UpdatePlanPricingRequest,
} from '../types/plans.types';
import type { IHttpResponse } from '@/core/api/HttpClient';
import httpClient from '@/core/api/createAxiosHttpClient';

export const plansApi = {
  /** Todos los planes, incluidos los inactivos y los privados, con su estado de sincronización. */
  getPlans: async (): Promise<IHttpResponse<Plan[]>> => {
    return httpClient.get<Plan[]>('/admin/plans');
  },

  getPlanById: async (planId: string): Promise<IHttpResponse<PlanDetail>> => {
    return httpClient.get<PlanDetail>(`/admin/plans/${planId}`);
  },

  createPlan: async (data: CreatePlanRequest): Promise<IHttpResponse<PlanDetail>> => {
    return httpClient.post<CreatePlanRequest, PlanDetail>('/admin/plans', data);
  },

  /** Solo nombre, descripcion y limites. Los precios van por su propio endpoint. */
  updatePlanDetails: async (
    planId: string,
    data: UpdatePlanDetailsRequest,
  ): Promise<IHttpResponse<void>> => {
    const response = await httpClient.put<UpdatePlanDetailsRequest>(`/admin/plans/${planId}`, data);
    return { ...response, data: undefined as unknown as void };
  },

  /** Reintenta la sincronizacion con Stripe. La respuesta trae el estado resultante. */
  retryPlanSync: async (planId: string): Promise<IHttpResponse<PlanDetail>> => {
    return httpClient.post<undefined, PlanDetail>(`/admin/plans/${planId}/sync`, undefined);
  },

  togglePlanStatus: async (planId: string, isActive: boolean): Promise<IHttpResponse<void>> => {
    const response = await httpClient.patch<{ isActive: boolean }>(
      `/admin/plans/${planId}/status`,
      { isActive },
    );
    return { ...response, data: undefined as unknown as void };
  },

  /** Cuantas cuentas estan hoy en el plan. Informativo: no cambia el cobro de ninguna. */
  getPricingImpact: async (planId: string): Promise<IHttpResponse<PlanPricingImpact>> => {
    return httpClient.get<PlanPricingImpact>(`/admin/plans/${planId}/pricing-impact`);
  },

  updatePlanPricing: async (
    planId: string,
    data: UpdatePlanPricingRequest,
  ): Promise<IHttpResponse<void>> => {
    const response = await httpClient.put<UpdatePlanPricingRequest>(
      `/admin/plans/${planId}/pricing`,
      data,
    );
    return { ...response, data: undefined as unknown as void };
  },

  getPriceMigrations: async (planId: string): Promise<IHttpResponse<PlanPriceMigration[]>> => {
    return httpClient.get<PlanPriceMigration[]>(`/admin/plans/${planId}/price-migrations`);
  },

  cancelPriceMigration: async (
    migrationId: string,
    reason?: string,
  ): Promise<IHttpResponse<void>> => {
    return httpClient.delete<void>(`/admin/plans/price-migrations/${migrationId}`, {
      reason: reason ?? null,
    });
  },
};
